import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Checkbox,
  FormHelperText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserToken } from "../../utils/account/common";
import CardInput from "./components/CardInput/CardInput";
import * as stripeJs from "@stripe/stripe-js";
import { CardElement } from "@stripe/react-stripe-js";
import { CreditCard, Receipt } from "@mui/icons-material";
import updateSubscription from "../../network/update-subscription";
import getOrganizations from "../../network/get-organizations";
import getCompanyOverview from "../../network/get-company-overview";
import addGTMEvent from "../../utils/gtm";
import StepTitleView from "../StepTitleView";
import RegistrationCell from "../registration-confirmation/components/RegistrationCell";

type PaymentType = "creditCard" | "invoice";

interface StripeObjects {
  stripe: stripeJs.Stripe;
  elements: stripeJs.StripeElements;
}

interface PaymentMethodProps {
  language: Language;
  signupInformation: SignupInformation;
  onSubmit: () => void;
}

/**
 * Log to GTM when subscrition is in production
 */
const logProductionAccount = (
  subscription: Subscription,
  organizationId: string
) => {
  if (subscription.status === "ACTIVE") {
    addGTMEvent({
      event: "productionAccount",
      orgId: organizationId,
    });
  }
};

const PaymentMethod = ({
  language,
  signupInformation,
  onSubmit,
}: PaymentMethodProps) => {
  const { t } = useTranslation();
  const [paymentType, setPaymentType] = useState<PaymentType>("invoice");
  const referenceInputRef = useRef<HTMLInputElement>(null);
  const [verificationChecked, setVerificationChecked] =
    useState<boolean>(false);
  const [referenceValid, setReferenceValid] = useState(false);
  const [referenceHasReceivedInput, setReferenceHasReceivedInput] =
    useState(false);
  const [cardValid, setCardValid] = useState(false);
  const [formSubmitError, setFormSubmitError] = useState<boolean>(false);
  const [showCheckBoxError, setShowCheckBoxError] = useState<boolean>(false);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [stripeObjects, setStripeObjects] = useState<
    undefined | StripeObjects
  >();
  const { data: organization } = useQuery({
    queryKey: ["organization"],
    queryFn: async () => {
      const userToken = (await getUserToken()) as string;
      return getOrganizations(userToken);
    },
  });
  const { data: companyOverview, isLoading: companyOverviewLoading } = useQuery(
    {
      queryKey: ["company-overview", signupInformation.companyId],
      queryFn: () =>
        getCompanyOverview(
          signupInformation.country,
          signupInformation.companyId
        ),
    }
  );

  /**
   * Correct possible invalid payment method choice when billing options
   * have been loaded.
   */
  useEffect(() => {
    const billingOptions = companyOverview?.properties.billingOptions ?? [];
    if (companyOverviewLoading) {
      return;
    }
    if (paymentType === "invoice" && !billingOptions.includes("send_invoice")) {
      setPaymentType("creditCard");
    }
    if (
      paymentType === "creditCard" &&
      !billingOptions.includes("charge_automatically")
    ) {
      setPaymentType("invoice");
    }
  }, [companyOverview, paymentType, setPaymentType, companyOverviewLoading]);

  const submitDisabled =
    (paymentType === "creditCard" && !cardValid) ||
    !referenceValid ||
    !verificationChecked;

  const onFormSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!organization) {
      return null;
    }
    if (!verificationChecked) {
      setShowCheckBoxError(true);
    }
    if (submitDisabled) {
      return null;
    }
    setFormSubmitting(true);
    try {
      const organizationId = organization.properties.elements[0].properties.id;
      const reference = referenceInputRef.current?.value ?? "";
      const userToken = (await getUserToken()) as string;

      if (paymentType === "invoice") {
        const subscription = await updateSubscription(
          userToken,
          organizationId,
          {
            reference,
            billing: "send_invoice",
          }
        );
        logProductionAccount(subscription.properties, organizationId);
      } else {
        if (stripeObjects) {
          const { stripe, elements } = stripeObjects;
          const cardElement = elements.getElement(CardElement);
          if (cardElement) {
            const { token, error } = await stripe.createToken(cardElement);
            if (error) {
              throw error;
            }
            if (!token) {
              throw new Error("failed getting card token");
            }
            const subscription = await updateSubscription(
              userToken,
              organizationId,
              {
                reference,
                billing: "charge_automatically",
                cardToken: token.id,
              }
            );
            logProductionAccount(subscription.properties, organizationId);
          }
        }
      }

      onSubmit();
      setFormSubmitError(false);
    } catch (e: any) {
      console.error(e);
      setFormSubmitError(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  const onChangeReference = (ref: React.RefObject<HTMLInputElement>) => () => {
    if (!ref.current) {
      return;
    }
    const valid = ref.current.checkValidity();
    setReferenceValid(valid);
    setReferenceHasReceivedInput(true);
  };

  const onCardChange = (e: CardInputChangeEvent) => setCardValid(e.complete);

  /**
   * Set fields ok on render in case some fields already have values
   */
  useEffect(() => {
    setReferenceValid((referenceInputRef.current?.value ?? "").length > 0);
    setReferenceHasReceivedInput(
      (referenceInputRef.current?.value ?? "").length > 0
    );
  }, []);

  const billingOptions = companyOverview?.properties.billingOptions ?? [];

  const address = `${signupInformation.streetAddress}, ${signupInformation.zip} ${signupInformation.city}`;
  return (
    <form autoComplete="off" noValidate onSubmit={onFormSubmit}>
      <StepTitleView
        title={t("step.paymentDetails.title")}
        descriptions={[t("step.paymentDetails.description")]}
      />
      <Stack alignItems="center" mb={6}>
        <Tabs
          TabIndicatorProps={{ sx: { height: 4 } }}
          value={paymentType}
          onChange={(_: React.SyntheticEvent, value: PaymentType) =>
            setPaymentType(value)
          }
        >
          <Tab
            sx={{ minHeight: 0 }}
            icon={<Receipt />}
            iconPosition="start"
            aria-label="Invoice"
            label={t("step.paymentMethod.invoice.label")}
            value="invoice"
            disabled={!billingOptions.includes("send_invoice")}
          />
          <Tab
            sx={{ minHeight: 0 }}
            icon={<CreditCard />}
            iconPosition="start"
            aria-label="Credit card"
            label={t("step.paymentMethod.creditCard.label")}
            value="creditCard"
            disabled={!billingOptions.includes("charge_automatically")}
          />
        </Tabs>
      </Stack>
      <Stack spacing={2} mb={2}>
        <RegistrationCell
          title={t(
            "step.registrationConfirmation.information.companyName.label"
          )}
          value={signupInformation.companyName}
          hasData={true}
          isLoading={false}
        />
        <RegistrationCell
          title={t(
            "step.registrationConfirmation.information.companyAddress.label"
          )}
          value={address}
          hasData={true}
          isLoading={false}
        />
        <RegistrationCell
          title={t("step.registrationConfirmation.information.reference.label")}
          value={signupInformation.email}
          hasData={true}
          isLoading={false}
        />
        {paymentType === "creditCard" && (
          <CardInput
            language={language}
            onLoad={(
              stripe: stripeJs.Stripe | null,
              elements: stripeJs.StripeElements | null
            ) => {
              if (!stripeObjects && stripe && elements) {
                setStripeObjects({ stripe, elements });
              }
            }}
            onChange={onCardChange}
          />
        )}
        <TextField
          required
          autoFocus={paymentType === "invoice"}
          label={t("step.paymentMethod.field.reference.placeholder")}
          fullWidth
          defaultValue={`${signupInformation.firstName} ${signupInformation.lastName}`}
          error={referenceHasReceivedInput && !referenceValid}
          inputRef={referenceInputRef}
          onChange={onChangeReference(referenceInputRef)}
        />
        <Stack
          alignItems="center"
          sx={{
            height: 120,
            justifyContent: "flex-start",
            alignItems: "flex-start",
          }}
          direction="row"
          spacing={1}
        >
          <Checkbox
            checked={verificationChecked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setVerificationChecked(e.target.checked);
              setShowCheckBoxError(false);
            }}
          />
          <Typography
            variant="body2"
            sx={{ cursor: "pointer" }}
            onClick={() => setVerificationChecked(!verificationChecked)}
          >
            {t("step.paymentMethod.field.verification.label")}{" "}
          </Typography>
        </Stack>
        {showCheckBoxError && (
          <FormHelperText sx={{ m: 0, color: "#DA2128", display: "flex" }}>
            {t("step.paymentMethod.checkBox.isRequired.message")}
          </FormHelperText>
        )}
      </Stack>
      {formSubmitError && (
        <Box mb={2} mt={2}>
          <Alert severity="error">
            {t("step.paymentMethod.feedback.error.")}
          </Alert>
        </Box>
      )}
      <Stack direction="column" alignItems="center" spacing={1}>
        <LoadingButton
          id="payment-method-continue"
          type="submit"
          variant="contained"
          loading={formSubmitting}
          fullWidth
        >
          {t("button.next")}
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default PaymentMethod;
